import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { useCachedCategoryInfo } from '@/module/public/hooks/useFetchCategoryInfo';
import { useCreateSubjectNameGetter } from '@/shared/category/useCreateSubjectNameGetter';
import { Chip, PublisherChip, SchoolInfoChip } from '@/shared/components/Chip/QuizInfoChip';
import { QuizListItemMetadata, QuizListItemThumbnail, QuizListItemTitle } from '@/shared/features/quiz-list';
interface QuizListItemWithActionsProps {
  quiz: SearchQuizsetRes;
  isLastItem: boolean;
  onQuizClick: (quizId: string) => void;
}

/**
 * QuizListItemWithActions - 퀴즈 리스트 아이템 (추천 섹션용, 액션 버튼 없음)
 *
 * @description
 * 추천 섹션(owner-quizzes, category-quizzes)에서 사용되는 퀴즈 카드입니다.
 * 액션 버튼을 제공하지 않고 클릭 시 퀴즈 상세 페이지로 이동합니다.
 *
 * @example
 * ```tsx
 * <QuizListItemWithActions
 *   quiz={quiz}
 *   isLastItem={index === quizzes.length - 1}
 *   onQuizClick={handleQuizClick}
 *   location="owner-quizzes"
 * />
 * ```
 */
export const QuizListItemWithActions = ({
  quiz,
  isLastItem,
  onQuizClick
}: QuizListItemWithActionsProps) => {
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const getSubjectName = useCreateSubjectNameGetter(categoryTreeData?.subjectTree);
  const subjectName = getSubjectName({
    subjectGroupId: quiz.categoryInfo?.subject,
    subSubjectGroupId: quiz.categoryInfo?.subSubject
  });
  return <div className="group relative flex cursor-pointer items-center gap-sm px-2xs py-none hover:bg-opacity-black-1 tablet-lg:gap-md tablet-lg:px-3xs" onClick={() => onQuizClick(quiz.id)} data-sentry-component="QuizListItemWithActions" data-sentry-source-file="QuizListItemWithActions.tsx">
      {/* 썸네일 */}
      <QuizListItemThumbnail thumbnailUrl={quiz.templateImageUrl || ''} alt={quiz.title} className="h-[52px] w-[79px] rounded-sm tablet-lg:h-[90px] tablet-lg:w-[137px] tablet-lg:rounded-md" data-sentry-element="QuizListItemThumbnail" data-sentry-source-file="QuizListItemWithActions.tsx" />

      {/* 콘텐츠 영역 */}
      <div className={`flex flex-1 flex-col gap-sm py-md tablet-lg:gap-xs tablet-lg:py-lg ${!isLastItem ? 'border-b border-alternative' : ''}`}>
        {/* Top section: Chips + Title */}
        <div className="flex flex-col gap-3xs tablet-lg:gap-2xs">
          {/* Chips row */}
          <div className="flex flex-wrap gap-3xs">
            {quiz.categoryInfo && <SchoolInfoChip className="text-caption-md-sb tablet-lg:text-body-sm-semibold" schoolInfo={quiz.categoryInfo} />}
            {subjectName && <Chip className="text-caption-md-sb tablet-lg:text-body-sm-semibold">
                {subjectName}
              </Chip>}
            <PublisherChip className="text-caption-md-sb tablet-lg:text-body-sm-semibold" hideEtc={true} publisherId={quiz.categoryInfo?.publisher} data-sentry-element="PublisherChip" data-sentry-source-file="QuizListItemWithActions.tsx" />
          </div>
          <QuizListItemTitle title={quiz.title} data-sentry-element="QuizListItemTitle" data-sentry-source-file="QuizListItemWithActions.tsx" />
        </div>

        {/* Bottom section: Author + Metadata */}
        <QuizListItemMetadata author={{
        name: quiz.authorUsername,
        avatar: undefined
      }} quizCount={quiz.quizCount} playCount={quiz.playCount} copyCount={quiz.copyCount} hideAuthorOnDesktop hideCopyCountOnMobile data-sentry-element="QuizListItemMetadata" data-sentry-source-file="QuizListItemWithActions.tsx" />
      </div>
    </div>;
};